import { processingProperties } from "../../lib/attribution/consent/us-policy/payload";
/**
 * TMG Owned Attribution — Browser Pixel SDK entry (design: pixel-sdk-design.md).
 *
 * Boot guard + command-queue drain + dispatcher. Everything is wrapped to fail
 * silently and never block page render. The pixel emits RAW events only.
 */
import type {
  ConsentState,
  IdentityTraits,
  InitOptions,
  ResolvedConfig,
  TrackEvent,
  TouchContext,
} from './types';
import { resolveConfig } from './config';
import { BehaviorRecorder } from './behavior';
import { PixelStore } from './storage';
import { Sender } from './transport';
import { mergeConsent, marketingGranted, unknownConsent } from './consent';
import { captureTouches, parseCampaign } from './campaign';
import { buildIdentity } from './identify';
import { readInbound, wireDecorate } from './crossdomain';
import { pathAllowed } from './path-policy';
import {
  currentUrl,
  byteLength,
  id as genId,
  nowIso,
  routeKey,
  urlWithoutQueryOrFragment,
  weakEntropy,
} from './util';
import { detectDebug, log, maskKey, maskTraits, setDebug, warn } from './debug';

const VERSION = '1.0.0';
const EVENT_NAME_RE = /^[A-Za-z][A-Za-z0-9_.:-]*$/;
const EVENT_NAME_MAX = 64;
const PROPERTY_BYTES_MAX = 8 * 1024;

function validTrackProperties(properties: unknown): properties is Record<string, unknown> | undefined {
  if (properties === undefined) return true;
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return false;
  try {
    const serialized = JSON.stringify(properties);
    return typeof serialized === 'string' && byteLength(serialized) <= PROPERTY_BYTES_MAX;
  } catch {
    return false;
  }
}

class Pixel {
  private cfg: ResolvedConfig | null = null;
  private behavior: BehaviorRecorder | null = null;
  private store: PixelStore | null = null;
  private sender: Sender | null = null;
  private consent: ConsentState = unknownConsent();
  private visitorId = '';
  private sessionId = '';
  private lastTrackedUrl = '';
  private touch: ReturnType<typeof captureTouches> | null = null;
  private campaign = { utm: {}, clickIds: {}, externalIds: {} } as ReturnType<typeof parseCampaign>;
  private analyticsIds: Record<string, string> = {};
  private booted = false;
  private lastRouteAllowed = true;

  dispatch(args: IArguments | unknown[]): void {
    try {
      const a = Array.prototype.slice.call(args as ArrayLike<unknown>);
      const cmd = a[0] as string;
      switch (cmd) {
        case 'init':
          this.init(a[1] as string, (a[2] as InitOptions) || {});
          break;
        case 'track':
          this.track(a[1] as string, a[2] as Record<string, unknown>, a[3] as { eventId?: string; occurredAt?: string } | undefined);
          break;
        case 'page':
          this.page(a[1] as Record<string, unknown>);
          break;
        case 'identify':
          this.identify(a[1] as IdentityTraits);
          break;
        case 'consent':
          this.updateConsent(a[1] as Partial<ConsentState>);
          break;
        case 'identifiers': {
          if (!this.sendAllowed() || this.consent.analytics !== true) break;
          const ids = a[1] as Record<string, unknown>;
          if (typeof ids?.gaClientId === 'string' && /^\d+\.\d+$/.test(ids.gaClientId)) this.analyticsIds.gaClientId = ids.gaClientId;
          if (typeof ids?.gaSessionId === 'string' && /^\d+$/.test(ids.gaSessionId)) this.analyticsIds.gaSessionId = ids.gaSessionId;
          break;
        }
        case 'setClientId':
          if (this.cfg) this.cfg.clientId = a[1] as string;
          break;
        case 'debug':
          setDebug(a[1] !== false);
          break;
        default:
          warn('unknown command', cmd);
      }
    } catch (e) {
      warn('dispatch error', e);
    }
  }

  private init(publicKey: string, opts: InitOptions): void {
    if (this.booted) {
      warn('init ignored — already initialized');
      return;
    }
    if (!publicKey) {
      warn('init missing public key');
      return;
    }
    this.booted = true;
    setDebug(detectDebug(opts.debug));

    this.cfg = resolveConfig(publicKey, opts);
    this.lastRouteAllowed = this.routeAllowed();
    this.consent = mergeConsent(unknownConsent(), opts.consent, opts.consent ? 'manual' : 'unknown');
    this.store = new PixelStore(this.cfg, () => this.consent);

    // Inbound cross-domain linker (adopt before generating fresh ids).
    const inbound = readInbound();

    this.visitorId = this.store.resolveVisitorId();
    // The linker is a cross-domain identifier: never adopt it without current
    // marketing consent, even though readInbound still strips it from the URL.
    const inboundAllowed = marketingGranted(this.consent) && this.routeAllowed();
    if (inboundAllowed && inbound && inbound.vid) {
      this.store.adoptVisitorId(inbound.vid);
      this.visitorId = this.store.vid;
    }
    const sess = this.store.resolveSession();
    this.sessionId = inboundAllowed && inbound && inbound.sid ? inbound.sid : sess.sessionId;

    // Campaign + touch context.
    if (this.routeAllowed()) this.capturePageContext();

    // Transport.
    this.sender = new Sender(
      this.cfg.collectorUrl,
      this.cfg.publicKey,
      this.store,
      () => ({ visitorId: this.visitorId, sessionId: this.sessionId }),
      (ids) => this.adoptServerIds(ids),
      () => this.sendAllowed(),
    );
    if (!this.routeAllowed()) this.sender.clear();

    log('init', {
      key: maskKey(publicKey),
      collector: this.cfg.collectorUrl,
      consentMode: this.cfg.consentMode,
      vid: this.visitorId,
      sid: this.sessionId,
      idConfidence: this.store.idConfidence,
      weakEntropy,
    });

    // Cross-domain link decoration (marketing-consent gated).
    wireDecorate(
      this.cfg.crossDomain,
      () => ({ vid: this.visitorId, sid: this.sessionId }),
      () => marketingGranted(this.consent) && this.routeAllowed(),
    );

    // New session ⇒ session_start before first page view.
    if (sess.isNew) this.emit(this.buildEvent('session_start'));

    // Initial page view (set lastTrackedUrl BEFORE arming SPA hooks → dedup).
    this.lastTrackedUrl = routeKey();
    if (this.cfg.autoPageView) this.emit(this.buildEvent('page_view'));
    if (this.cfg.spaTracking) this.armSpa();
    this.armLifecycle();
    if (this.cfg.behavior) this.behavior = new BehaviorRecorder(
      new URL('/api/track/v1/behavior', this.cfg.collectorUrl).toString(), this.cfg.publicKey,
      () => ({ allowed: this.consent.analytics === true && this.routeAllowed(), visitorId: this.visitorId, sessionId: this.sessionId, marketing: this.consent.marketing === true && this.consent.saleOrShare !== true, receipt: this.consent.receiptId && this.consent.receiptProof ? { id: this.consent.receiptId, proof: this.consent.receiptProof } : undefined }),
    );
  }

  private track(name: string, properties?: Record<string, unknown>, options?: { eventId?: string; occurredAt?: string }): void {
    if (!this.ready() || !this.routeAllowed() || !name || typeof name !== 'string') {
      if (!this.routeAllowed() && this.sender) this.sender.clear();
      warn('track ignored', name);
      return;
    }
    if (name.length > EVENT_NAME_MAX || !EVENT_NAME_RE.test(name)) {
      warn('track ignored — event name must start with a letter, use letters/digits/_.:- and be at most 64 characters', name);
      return;
    }
    if (!validTrackProperties(properties)) {
      warn('track ignored — properties must be a JSON object no larger than 8KB', name);
      return;
    }
    const event = this.buildEvent(name, properties);
    if (options?.eventId !== undefined) {
      if (typeof options.eventId !== 'string' || !options.eventId || options.eventId.length > 512) return;
      event.eventId = options.eventId;
    }
    if (options?.occurredAt) {
      if (!Number.isFinite(Date.parse(options.occurredAt))) return;
      event.occurredAt = options.occurredAt;
    }
    this.emit(event);
  }

  private page(properties?: Record<string, unknown>): void {
    if (!this.ready() || !this.routeAllowed()) {
      if (this.sender) this.sender.clear();
      return;
    }
    const force = !!(properties && (properties as { force?: boolean }).force);
    const key = routeKey();
    if (!force && key === this.lastTrackedUrl) return;
    this.lastTrackedUrl = key;
    this.touchActivity();
    this.emit(this.buildEvent('page_view', properties));
    this.behavior?.restart();
  }

  private identify(traits: IdentityTraits): void {
    if (!this.cfg?.identityAllowed) return;
    if (!this.ready() || !this.routeAllowed() || !traits) {
      if (!this.routeAllowed() && this.sender) this.sender.clear();
      return;
    }
    log("identify", maskTraits(traits));
    const base = this.buildEvent("identify");
    buildIdentity(traits)
      .then((idp) => {
        base.identity = idp;
        this.emit(base);
      })
      .catch(() => {
        // Never send raw PII; if hashing failed, send only externalLeadId.
        base.identity = traits.externalLeadId
          ? { externalLeadId: String(traits.externalLeadId) }
          : undefined;
        this.emit(base);
      });
  }

  private updateConsent(patch: Partial<ConsentState>): void {
    const wasAllowed = this.sendAllowed();
    if (!this.consent.receiptId && (this.consent.analytics === true || this.consent.marketing === true) && (patch.analytics === false || patch.marketing === false || patch.saleOrShare === true) && this.cfg && this.visitorId) {
      const body = JSON.stringify({ key: this.cfg.publicKey, visitorId: this.visitorId });
      const url = new URL('/api/track/v1/privacy', this.cfg.collectorUrl).toString();
      // A withdrawal must reach the server before retiring this browser identity.
      // It carries no browsing data and is independent of the analytics sending gate.
      const sendWithdrawal = (attempt = 0) => {
        void fetch(url, { method: 'POST', headers: { 'content-type': 'text/plain' }, body, keepalive: true, credentials: 'omit' })
          .then((response) => { if (!response.ok) throw new Error('privacy delivery failed'); })
          .catch(() => { if (attempt < 3) setTimeout(() => sendWithdrawal(attempt + 1), 1000 * 2 ** attempt); });
      };
      sendWithdrawal();
    }
    this.consent = mergeConsent(this.consent, patch, 'manual');
    if (this.consent.analytics !== true) this.analyticsIds = {};
    log('consent', this.consent);
    // Grant: promote the existing ephemeral id. Withdrawal: retire an active
    // persistent identity and continue with fresh page/session-scoped ids.
    if (this.store) {
      const identityRotated = this.store.reconcileConsent();
      this.visitorId = this.store.vid;
      if (identityRotated) this.sessionId = this.store.resolveSession().sessionId;
    }
    // Re-resolve campaign data at the consent boundary. This captures ad IDs
    // only after a grant and removes them immediately after withdrawal.
    if (this.cfg && this.store && this.routeAllowed()) this.capturePageContext();
    else this.clearPageContext();
    this.behavior?.refreshConsent();
    const isAllowed = this.sendAllowed();
    if (!isAllowed && this.sender) this.sender.clear();
    if (this.ready() && isAllowed) {
      this.emit(this.buildEvent('consent_updated'));
      // A regulated hard gate intentionally dropped the pre-consent bootstrap.
      // Start measurement at the grant boundary with the current page only.
      if (!wasAllowed) {
        this.emit(this.buildEvent('session_start'));
        if (this.cfg?.autoPageView) this.emit(this.buildEvent('page_view'));
      }
    }
  }

  /* ----------------------------- internals ----------------------------- */

  private adoptServerIds(ids: { visitorId?: string; sessionId?: string }): void {
    if (ids.visitorId && ids.visitorId !== this.visitorId && this.store) {
      this.store.adoptVisitorId(ids.visitorId);
      this.visitorId = this.store.vid;
      log('adopted server visitorId', this.visitorId);
    }
    if (ids.sessionId && ids.sessionId !== this.sessionId) {
      this.sessionId = ids.sessionId;
    }
  }

  private buildEvent(
    name: string,
    properties?: Record<string, unknown>,
  ): TrackEvent {
    const ev: TrackEvent = {
      event: name,
      eventId: genId("evt"),
      occurredAt: nowIso(),
      url: currentUrl(),
      path: safePath(),
      referrer: this.cfg?.metadataOnly ? undefined : safeRef(),
      title: this.cfg?.metadataOnly ? undefined : safeTitle(),
      consent: this.consent,
    };
    if (!this.cfg?.metadataOnly && Object.keys(this.campaign.utm).length)
      ev.utm = this.campaign.utm;
    const externalIds = {
      ...this.campaign.externalIds,
      ...(this.consent.analytics === true ? this.analyticsIds : {}),
    };
    if (Object.keys(externalIds).length) ev.externalIds = externalIds;
    ev.context = this.cfg?.metadataOnly
      ? { lib: { name: "tmg-pixel", version: VERSION } }
      : this.context();
    if (this.cfg?.metadataOnly) ev.properties = processingProperties(this.consent.collectionFields, properties);
    if (!this.cfg?.metadataOnly && properties && typeof properties === "object")
      ev.properties = properties;
    return ev;
  }

  private context(): Record<string, unknown> {
    const ctx: Record<string, unknown> = {
      lib: { name: 'tmg-pixel', version: VERSION },
      idConfidence: this.store ? this.store.idConfidence : 'low',
    };
    try {
      ctx.screen = { width: screen.width, height: screen.height };
      ctx.viewport = { width: innerWidth, height: innerHeight };
      ctx.language = navigator.language;
    } catch {
      /* noop */
    }
    try {
      ctx.tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      /* noop */
    }
    if (this.touch) {
      const includeClickIds = marketingGranted(this.consent);
      if (this.touch.firstTouch) {
        const firstTouch = consentSafeTouch(
          this.touch.firstTouch,
          includeClickIds,
          this.cfg?.sensitiveMode ? this.cfg.pathAllowlist : undefined,
        );
        if (firstTouch) ctx.firstTouch = firstTouch;
      }
      if (this.touch.lastTouch) {
        const lastTouch = consentSafeTouch(
          this.touch.lastTouch,
          includeClickIds,
          this.cfg?.sensitiveMode ? this.cfg.pathAllowlist : undefined,
        );
        if (lastTouch) ctx.lastTouch = lastTouch;
      }
      if (this.touch.utmInternalReferrer) ctx.utmInternalReferrer = true;
    }
    return ctx;
  }

  private emit(ev: TrackEvent): void {
    try {
      if (!this.routeAllowed()) {
        if (this.sender) this.sender.clear();
        return;
      }
      this.touchActivity();
      if (this.sender) this.sender.enqueue(ev);
      if (this.isDebugEcho()) log('event', ev.event, ev);
    } catch (e) {
      warn('emit error', e);
    }
  }

  private sendAllowed(): boolean {
    const consentAllowed = !this.cfg?.clientHardGate || this.consent.analytics === true;
    return consentAllowed && this.routeAllowed();
  }

  private routeAllowed(): boolean {
    if (
      this.cfg?.collectionPaths !== undefined &&
      !pathAllowed(
        safePath().replace(/\/+$/, "") || "/",
        this.cfg.collectionPaths,
      )
    )
      return false;
    if (!this.cfg?.sensitiveMode) return true;
    return pathAllowed(safePath(), this.cfg.pathAllowlist);
  }

  private capturePageContext(): void {
    if (!this.cfg || !this.store) return;
    if (this.cfg.metadataOnly) {
      this.clearPageContext();
      return;
    }
    this.campaign = parseCampaign(
      this.cfg.paramAllowlist,
      marketingGranted(this.consent),
    );
    this.touch = captureTouches(this.store, this.campaign);
  }

  private clearPageContext(): void {
    this.campaign = { utm: {}, clickIds: {}, externalIds: {} };
    this.touch = null;
  }

  private isDebugEcho(): boolean {
    return true; // log() itself is a no-op unless debug is on
  }

  private touchActivity(): void {
    if (this.store) this.store.touchSession();
  }

  private armSpa(): void {
    try {
      const fire = () => {
        const key = routeKey();
        const wasRouteAllowed = this.lastRouteAllowed;
        const isRouteAllowed = this.routeAllowed();
        this.lastRouteAllowed = isRouteAllowed;
        if (!isRouteAllowed) {
          this.lastTrackedUrl = key;
          this.clearPageContext();
          if (this.sender) this.sender.clear();
          return;
        }
        if (key !== this.lastTrackedUrl) {
          this.lastTrackedUrl = key;
          if (!wasRouteAllowed) this.capturePageContext();
          this.touchActivity();
          if (!wasRouteAllowed) this.emit(this.buildEvent('session_start'));
          this.emit(this.buildEvent('page_view'));
        }
      };
      const patch = (name: 'pushState' | 'replaceState') => {
        const orig = history[name];
        history[name] = function (this: History, ...rest: unknown[]) {
          const r = (orig as (...a: unknown[]) => unknown).apply(this, rest);
          try {
            dispatchEvent(new Event('tmg:locationchange'));
          } catch {
            /* noop */
          }
          return r;
        } as History[typeof name];
      };
      patch('pushState');
      patch('replaceState');
      addEventListener('popstate', fire);
      addEventListener('tmg:locationchange', fire);
    } catch {
      /* noop */
    }
  }

  private armLifecycle(): void {
    try {
      addEventListener('pagehide', () => {
        if (!this.routeAllowed()) {
          if (this.sender) this.sender.clear();
          return;
        }
        if (this.sender) {
          this.emit(this.buildEvent('session_end'));
          this.sender.flush(true);
        }
      });
    } catch {
      /* noop */
    }
  }

  private ready(): boolean {
    if (!this.booted || !this.cfg || !this.sender) {
      warn('command before init');
      return false;
    }
    return true;
  }
}

/* ------------------------------- boot ------------------------------- */

(function boot(w: Window & { __tmgLoaded?: boolean; tmg?: unknown; tmgq?: unknown }): void {
  try {
    if (w.__tmgLoaded) return;
    w.__tmgLoaded = true;

    const pixels: Record<string, Pixel> = Object.create(null);
    const dispatch = (args: ArrayLike<unknown>): string | undefined => {
      const a = Array.prototype.slice.call(args) as unknown[];
      if (a[0] === 'init') {
        const key = a[1] as string;
        const opts = (a[2] || {}) as InitOptions;
        const name = opts.name || key;
        if (!key || pixels[name]) return;
        const pixel = new Pixel();
        const first = Object.keys(pixels).length === 0;
        pixels[name] = pixel;
        pixel.dispatch(['init', key, { ...opts, storageNamespace: opts.storageNamespace ?? (first ? undefined : key.replace(/[^a-zA-Z0-9]/g, '').slice(-24)) }]);
        return;
      }
      const options = (a[3] || {}) as { eventId?: string; pixel?: string };
      let eventId: string | undefined;
      if (a[0] === 'track') {
        eventId = options.eventId || genId('evt');
        a[3] = { ...options, eventId };
      }
      const targets = options.pixel ? [pixels[options.pixel]].filter(Boolean) : Object.values(pixels);
      for (const pixel of targets) pixel.dispatch(a);
      return eventId;
    };
    const queued = (w.tmgq as unknown[]) || [];

    w.tmg = function () {
      // eslint-disable-next-line prefer-rest-params
      return dispatch(arguments);
    };
    // Drain anything buffered before v1.js loaded, in FIFO order.
    if (queued && typeof (queued as { length?: number }).length === 'number') {
      for (let i = 0; i < (queued as unknown[]).length; i++) {
        dispatch((queued as unknown[])[i] as unknown[]);
      }
    }
    // Keep a live sink so late pushes still dispatch.
    w.tmgq = {
      push: function () {
        // eslint-disable-next-line prefer-rest-params
        dispatch(arguments);
        return 0;
      },
    };
  } catch {
    /* fail silently — never block the host page */
  }
})(window as Window & { __tmgLoaded?: boolean; tmg?: unknown; tmgq?: unknown });

function safePath(): string {
  try {
    return location.pathname;
  } catch {
    return '';
  }
}
function safeRef(): string {
  try {
    return urlWithoutQueryOrFragment(document.referrer || '');
  } catch {
    return '';
  }
}
function safeTitle(): string {
  try {
    return document.title || '';
  } catch {
    return '';
  }
}

/** Remove legacy raw URLs and marketing IDs from persisted touch records. */
function consentSafeTouch(
  touch: TouchContext,
  includeClickIds: boolean,
  approvedPaths?: readonly string[],
): TouchContext | null {
  const clean: TouchContext = { ...touch };
  if (approvedPaths) {
    if (!clean.landingUrl) return null;
    try {
      if (!pathAllowed(new URL(clean.landingUrl).pathname, approvedPaths)) return null;
    } catch {
      return null;
    }
  }
  if (clean.landingUrl) clean.landingUrl = urlWithoutQueryOrFragment(clean.landingUrl);
  if (clean.referrer) clean.referrer = urlWithoutQueryOrFragment(clean.referrer);
  if (!includeClickIds) delete clean.clickIds;
  return clean;
}

export { Pixel };
